import { publicApi, sellerApi } from '@core/api/httpClient'
import { supportEndpointPaths } from './supportEndpointPaths'
import type {
  ApiEnvelope,
  FaqCategoryDto,
  FaqListDto,
  FaqsQueryParams,
} from './contracts/faqContracts'
import type {
  CreatePublicSupportTicketRequest,
  CreatePublicSupportTicketResponseDto,
  CreateSupportTicketRequest,
  CreateSupportTicketResponseDto,
  SupportTicketDetailDto,
  SupportTicketsListDto,
  SupportTicketsQueryParams,
  TicketCategoryDto,
} from './contracts/supportTicketContracts'

export async function getFaqCategories(): Promise<FaqCategoryDto[]> {
  const { data } = await sellerApi.get<ApiEnvelope<FaqCategoryDto[]>>(
    supportEndpointPaths.FAQ_CATEGORIES,
  )
  return data.data
}

export async function getFaqs(params: FaqsQueryParams): Promise<FaqListDto> {
  const { data } = await sellerApi.get<ApiEnvelope<FaqListDto>>(supportEndpointPaths.FAQS, {
    params,
  })
  return data.data
}

// Public on the server — the logged-out contact form loads it too — so it goes
// through `publicApi`, which never bounces an anonymous reader to /login.
export async function getTicketCategories(): Promise<TicketCategoryDto[]> {
  const { data } = await publicApi.get<ApiEnvelope<TicketCategoryDto[]>>(
    supportEndpointPaths.TICKET_CATEGORIES,
  )
  return data.data
}

export async function getSupportTickets(
  params: SupportTicketsQueryParams,
): Promise<SupportTicketsListDto> {
  const { data } = await sellerApi.get<ApiEnvelope<SupportTicketsListDto>>(
    supportEndpointPaths.TICKETS,
    { params },
  )
  return data.data
}

export async function getSupportTicket(id: string): Promise<SupportTicketDetailDto> {
  const { data } = await sellerApi.get<ApiEnvelope<SupportTicketDetailDto>>(
    supportEndpointPaths.TICKET_DETAIL(id),
  )
  return data.data
}

// Multipart because of the optional attachment; axios sets the boundary. The
// requester's name and email come from the account server-side, so they are
// not sent.
export async function createSupportTicket(
  request: CreateSupportTicketRequest,
): Promise<CreateSupportTicketResponseDto> {
  const formData = new FormData()
  formData.append('categoryId', request.categoryId)
  formData.append('subject', request.subject.trim())
  formData.append('description', request.description.trim())
  if (request.attachment) formData.append('attachment', request.attachment)

  const { data } = await sellerApi.post<ApiEnvelope<CreateSupportTicketResponseDto>>(
    supportEndpointPaths.TICKETS,
    formData,
  )
  return data.data
}

export async function createPublicSupportTicket(
  request: CreatePublicSupportTicketRequest,
): Promise<CreatePublicSupportTicketResponseDto> {
  const { data } = await publicApi.post<ApiEnvelope<CreatePublicSupportTicketResponseDto>>(
    supportEndpointPaths.PUBLIC_TICKETS,
    {
      name: request.name.trim(),
      email: request.email.trim(),
      categoryId: request.categoryId,
      subject: request.subject.trim(),
      description: request.description.trim(),
    },
  )
  return data.data
}
